// modules/Title/TitleSettings.js
// 标题场景设置模块

import SettingsHelper from '../SettingsHelper.js';

export default class TitleSettings {
    constructor(scene) {
        this.scene = scene;
        this.settingsElements = null;
        this.settingsEscKey = null;
    }
    
    /**
     * 显示设置界面
     * @param {Function} onClose - 关闭回调
     */
    showSettings(onClose = null) {
        // 先清理之前的设置UI（如果存在）
        this.closeSettings();
        
        // 获取当前设置
        const settings = SettingsHelper.getSettings();
        
        // 初始化设置元素对象
        this.settingsElements = {
            bg: null,
            title: null,
            backButton: null,
            sliders: [],
            toggles: []
        };
        
        // 创建设置界面背景
        const bg = this.scene.add.graphics();
        bg.fillStyle(0x000000, 0.9);
        bg.fillRect(
            this.scene.cameras.main.centerX - 300,
            this.scene.cameras.main.centerY - 200,
            600,
            400
        );
        
        bg.lineStyle(3, 0x4cc9f0, 1);
        bg.strokeRect(
            this.scene.cameras.main.centerX - 300,
            this.scene.cameras.main.centerY - 200,
            600,
            400
        );
        
        // 标题
        const title = this.scene.add.text(
            this.scene.cameras.main.centerX,
            this.scene.cameras.main.centerY - 170,
            '游戏设置',
            {
                fontSize: '24px',
                fill: '#4cc9f0',
                fontFamily: 'monospace',
                fontWeight: 'bold'
            }
        ).setOrigin(0.5);
        
        const startY = this.scene.cameras.main.centerY - 100;
        const spacing = 60;
        
        // 音效音量
        this.createVolumeSlider(
            '音效音量',
            settings.soundVolume,
            this.scene.cameras.main.centerX - 250,
            startY,
            (value) => {
                settings.soundVolume = value;
                this.updateSettings(settings);
            }
        );
        
        // BGM音量
        this.createVolumeSlider(
            '背景音乐',
            settings.musicVolume,
            this.scene.cameras.main.centerX - 250,
            startY + spacing,
            (value) => {
                settings.musicVolume = value;
                this.updateSettings(settings);
                // 实时更新当前BGM音量
                if (this.onBGMVolumeChange) {
                    this.onBGMVolumeChange(value);
                }
            }
        );
        
        // 战斗消息开关
        this.createToggleSwitch(
            '战斗消息',
            settings.battleMessages,
            this.scene.cameras.main.centerX - 250,
            startY + spacing * 2,
            (value) => {
                settings.battleMessages = value;
                this.updateSettings(settings);
            }
        );
        
        // 自动战斗开关
        this.createToggleSwitch(
            '自动战斗',
            settings.autoBattle,
            this.scene.cameras.main.centerX - 250,
            startY + spacing * 3,
            (value) => {
                settings.autoBattle = value;
                this.updateSettings(settings);
            }
        );
        
        // 返回按钮
        const backButton = this.scene.add.text(
            this.scene.cameras.main.centerX,
            this.scene.cameras.main.centerY + 150,
            '返回 (ESC)',
            {
                fontSize: '18px',
                fill: '#ffffff',
                fontFamily: 'monospace',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                padding: { x: 20, y: 10 }
            }
        ).setOrigin(0.5);
        backButton.setInteractive({ useHandCursor: true });
        
        backButton.on('pointerdown', () => {
            this.closeSettings();
            if (onClose) onClose();
        });
        
        // 保存设置元素
        this.settingsElements.bg = bg;
        this.settingsElements.title = title;
        this.settingsElements.backButton = backButton;
        
        // ESC键关闭
        this.settingsEscKey = this.scene.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
        this.settingsEscKey.on('down', () => {
            this.closeSettings();
            if (onClose) onClose();
        });
    }
    
    /**
     * 创建音量滑块
     * @param {string} label - 标签
     * @param {number} initialValue - 初始值
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     * @param {Function} onChange - 值改变回调
     */
    createVolumeSlider(label, initialValue, x, y, onChange) {
        // 标签
        const labelText = this.scene.add.text(
            x,
            y,
            label + ':',
            {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'monospace'
            }
        );
        
        // 滑块背景
        const sliderBg = this.scene.add.graphics();
        sliderBg.fillStyle(0x333333, 1);
        sliderBg.fillRect(x + 120, y - 10, 200, 20);
        sliderBg.lineStyle(2, 0x4cc9f0, 1);
        sliderBg.strokeRect(x + 120, y - 10, 200, 20);
        
        // 滑块值文本
        const valueText = this.scene.add.text(
            x + 330,
            y,
            Math.round(initialValue * 100) + '%',
            {
                fontSize: '16px',
                fill: '#4cc9f0',
                fontFamily: 'monospace'
            }
        );
        
        // 滑块按钮
        const sliderX = x + 120 + (initialValue * 200);
        const sliderButton = this.scene.add.circle(sliderX, y, 10, 0x4cc9f0);
        sliderButton.setInteractive({ useHandCursor: true });
        
        // 可拖拽区域
        const dragArea = this.scene.add.rectangle(x + 120 + 100, y, 200, 30, 0x000000, 0);
        dragArea.setInteractive();
        
        let isDragging = false;
        
        const updateSlider = (pointer) => {
            if (!isDragging) return;
            
            const localX = pointer.x - (x + 120);
            const value = Phaser.Math.Clamp(localX / 200, 0, 1);
            
            sliderButton.x = x + 120 + (value * 200);
            valueText.setText(Math.round(value * 100) + '%');
            
            onChange(value);
        };
        
        sliderButton.on('pointerdown', (pointer) => {
            isDragging = true;
        });
        
        dragArea.on('pointerdown', (pointer) => {
            isDragging = true;
            updateSlider(pointer);
        });
        
        this.scene.input.on('pointermove', (pointer) => {
            if (isDragging && pointer.isDown) {
                updateSlider(pointer);
            }
        });
        
        this.scene.input.on('pointerup', () => {
            isDragging = false;
        });
        
        // 保存到设置元素中以便清理
        if (!this.settingsElements) {
            this.settingsElements = { sliders: [], toggles: [] };
        }
        if (!this.settingsElements.sliders) {
            this.settingsElements.sliders = [];
        }
        this.settingsElements.sliders.push({
            label: labelText,
            bg: sliderBg,
            valueText: valueText,
            button: sliderButton,
            dragArea: dragArea
        });
    }
    
    /**
     * 创建开关
     * @param {string} label - 标签
     * @param {boolean} initialValue - 初始值
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     * @param {Function} onChange - 值改变回调
     */
    createToggleSwitch(label, initialValue, x, y, onChange) {
        // 标签
        const labelText = this.scene.add.text(
            x,
            y,
            label + ':',
            {
                fontSize: '16px',
                fill: '#ffffff',
                fontFamily: 'monospace'
            }
        );
        
        // 开关背景
        const switchBg = this.scene.add.graphics();
        const switchWidth = 60;
        const switchHeight = 30;
        const switchX = x + 120;
        const switchY = y;
        
        const updateSwitch = (value) => {
            switchBg.clear();
            if (value) {
                switchBg.fillStyle(0x00ff00, 1);
            } else {
                switchBg.fillStyle(0x666666, 1);
            }
            switchBg.fillRoundedRect(switchX, switchY - switchHeight/2, switchWidth, switchHeight, 15);
            switchBg.lineStyle(2, 0x4cc9f0, 1);
            switchBg.strokeRoundedRect(switchX, switchY - switchHeight/2, switchWidth, switchHeight, 15);
            
            // 开关按钮
            const buttonX = value ? switchX + switchWidth - 20 : switchX + 20;
            switchBg.fillStyle(0xffffff, 1);
            switchBg.fillCircle(buttonX, switchY, 12);
        };
        
        updateSwitch(initialValue);
        
        // 可点击区域
        const clickArea = this.scene.add.rectangle(switchX + switchWidth/2, switchY, switchWidth, switchHeight + 10, 0x000000, 0);
        clickArea.setInteractive({ useHandCursor: true });
        
        let currentValue = initialValue;
        
        clickArea.on('pointerdown', () => {
            currentValue = !currentValue;
            updateSwitch(currentValue);
            onChange(currentValue);
        });
        
        // 保存到设置元素中以便清理
        if (!this.settingsElements) {
            this.settingsElements = { sliders: [], toggles: [] };
        }
        if (!this.settingsElements.toggles) {
            this.settingsElements.toggles = [];
        }
        this.settingsElements.toggles.push({
            label: labelText,
            bg: switchBg,
            clickArea: clickArea,
            getValue: () => currentValue
        });
    }
    
    /**
     * 更新设置
     * @param {Object} settings - 设置对象
     */
    updateSettings(settings) {
        // 使用SettingsHelper统一更新设置（会自动保存到localStorage）
        SettingsHelper.updateSettings(settings);
    }
    
    /**
     * 设置BGM音量改变回调
     * @param {Function} callback - 回调函数 (volume) => void
     */
    setBGMVolumeChangeCallback(callback) {
        this.onBGMVolumeChange = callback;
    }
    
    /**
     * 关闭设置界面
     */
    closeSettings() {
        // 清理设置界面元素
        if (this.settingsElements) {
            if (this.settingsElements.bg) this.settingsElements.bg.destroy();
            if (this.settingsElements.title) this.settingsElements.title.destroy();
            if (this.settingsElements.backButton) this.settingsElements.backButton.destroy();
            
            if (this.settingsElements.sliders) {
                this.settingsElements.sliders.forEach(slider => {
                    if (slider.label) slider.label.destroy();
                    if (slider.bg) slider.bg.destroy();
                    if (slider.valueText) slider.valueText.destroy();
                    if (slider.button) slider.button.destroy();
                    if (slider.dragArea) slider.dragArea.destroy();
                });
            }
            
            if (this.settingsElements.toggles) {
                this.settingsElements.toggles.forEach(toggle => {
                    if (toggle.label) toggle.label.destroy();
                    if (toggle.bg) toggle.bg.destroy();
                    if (toggle.clickArea) toggle.clickArea.destroy();
                });
            }
            
            this.settingsElements = null;
        }
        
        // 移除ESC键监听
        if (this.settingsEscKey) {
            this.settingsEscKey.removeAllListeners();
            this.settingsEscKey = null;
        }
    }
}

